import { useState, useEffect, useMemo } from 'react'
import {
  Search,
  Filter,
  Beef,
  Carrot,
  Wheat,
  Droplets,
  ChefHat,
  ChevronRight,
  X
} from 'lucide-react'
import { useToastActions } from '../components/Toast'

// ══════════════════════════════════════════════════════════════════════════════
// CATEGORY ICONS & COLORS
// ══════════════════════════════════════════════════════════════════════════════

const categoryConfig = {
  proteins: { icon: Beef, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
  vegetables: { icon: Carrot, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
  carbs: { icon: Wheat, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  sauces: { icon: Droplets, color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-200' },
  bases: { icon: ChefHat, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENT CARD
// ══════════════════════════════════════════════════════════════════════════════

function ComponentCard({ component, category, onClick }) {
  const config = categoryConfig[category] || categoryConfig.bases
  const Icon = config.icon

  return (
    <button
      onClick={() => onClick(component)}
      className={`w-full text-left p-4 rounded-xl border ${config.border} ${config.bg} hover:shadow-soft transition-all group`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg bg-white flex items-center justify-center ${config.color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-havn-charcoal group-hover:text-havn-green-700 transition-colors">
            {component.name}
          </h4>
          <p className="text-sm text-havn-gray-500 mt-0.5 truncate">
            {component.description || `${component.portion_oz}oz portion`}
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-havn-gray-400">
              Used in {component.dishes?.length || 0} dishes
            </span>
            {component.recipe_name && (
              <span className="text-xs px-2 py-0.5 bg-white rounded-full text-havn-gray-500">
                Has recipe
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-havn-gray-300 group-hover:text-havn-green-700 transition-colors" />
      </div>
    </button>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENT DETAIL MODAL
// ══════════════════════════════════════════════════════════════════════════════

function ComponentDetailModal({ component, category, onClose, onNavigate }) {
  if (!component) return null

  const config = categoryConfig[category] || categoryConfig.bases
  const Icon = config.icon

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal max-w-2xl">
        <div className="modal-header">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${config.bg} ${config.color} flex items-center justify-center`}>
              <Icon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold text-havn-green-900">
                {component.name}
              </h3>
              <p className="text-sm text-havn-gray-500 capitalize">{category.slice(0, -1)}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-havn-gray-400 hover:text-havn-charcoal hover:bg-havn-cream-200 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="modal-body space-y-6">
          {/* Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-havn-cream-50 rounded-lg">
              <span className="text-xs text-havn-gray-500 uppercase tracking-wide">Portion Size</span>
              <p className="font-semibold text-havn-charcoal">{component.portion_oz} oz</p>
            </div>
            <div className="p-3 bg-havn-cream-50 rounded-lg">
              <span className="text-xs text-havn-gray-500 uppercase tracking-wide">Order Factor</span>
              <p className="font-semibold text-havn-charcoal">{Number(component.order_factor_lbs).toFixed(3)} {component.order_factor_portion_oz ? 'lb / 6 oz portion' : 'lbs/meal'}</p>
              {component.order_factor_portion_oz && <p className="text-xs text-havn-gray-500">{component.order_factor_basis === 'legacy_6oz' ? 'Legacy allowance; existing yield adjustment retained.' : 'Raw weight, including cooking loss.'}</p>}
            </div>
          </div>

          {/* Description */}
          {component.description && (
            <div>
              <h4 className="text-sm font-semibold text-havn-charcoal mb-2">Description</h4>
              <p className="text-sm text-havn-gray-600">{component.description}</p>
            </div>
          )}

          {/* Used In Dishes */}
          {component.dishes?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-havn-charcoal mb-3">
                Used in {component.dishes.length} dishes
              </h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {component.dishes.map((dish, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-havn-cream-50 rounded-lg">
                    <span className="font-medium text-sm text-havn-charcoal">{dish.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full capitalize
                      ${dish.category === 'chicken' ? 'bg-green-100 text-green-700' :
                        dish.category === 'beef' ? 'bg-red-100 text-red-700' :
                        dish.category === 'seafood' ? 'bg-cyan-100 text-cyan-700' :
                        'bg-gray-100 text-gray-700'}`}>
                      {dish.category}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recipe Link */}
          {component.recipe_name && (
            <div className="p-4 bg-havn-brass-400/10 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-havn-brass-600 uppercase tracking-wide">Associated Recipe</span>
                  <p className="font-semibold text-havn-charcoal">{component.recipe_name}</p>
                </div>
                <button
                  className="btn-secondary text-sm"
                  onClick={() => {
                    onClose()
                    if (onNavigate) onNavigate('recipes')
                  }}
                >
                  View Recipe
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════

export default function ComponentLibrary({ onNavigate }) {
  const [components, setComponents] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeCategory, setActiveCategory] = useState(null)
  const [selectedComponent, setSelectedComponent] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const { error } = useToastActions()

  // Load components
  useEffect(() => {
    async function loadComponents() {
      try {
        setLoading(true)
        const res = await fetch('/api/components/')
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        setComponents(data)
      } catch (err) {
        error('Failed to load components')
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    loadComponents()
  }, [])

  // Filter components
  const filteredComponents = useMemo(() => {
    if (!components) return null

    const grouped = { ...components.grouped }

    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      Object.keys(grouped).forEach(cat => {
        grouped[cat] = grouped[cat].filter(c =>
          c.name.toLowerCase().includes(term) ||
          c.description?.toLowerCase().includes(term)
        )
      })
    }

    return grouped
  }, [components, searchTerm])

  // Category tabs
  const categories = [
    { id: 'proteins', label: 'Proteins', icon: Beef },
    { id: 'vegetables', label: 'Vegetables', icon: Carrot },
    { id: 'carbs', label: 'Carbs', icon: Wheat },
    { id: 'sauces', label: 'Sauces', icon: Droplets },
    { id: 'bases', label: 'Bases', icon: ChefHat },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-havn-green-700" />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {categories.map(cat => {
          const count = components?.counts?.[cat.id] || 0
          const config = categoryConfig[cat.id]
          const Icon = cat.icon
          const isActive = activeCategory === cat.id

          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(isActive ? null : cat.id)}
              className={`p-3 rounded-xl border transition-all text-left
                ${isActive
                  ? `${config.bg} ${config.border} shadow-soft`
                  : 'bg-white border-havn-cream-300 hover:border-havn-gray-300'
                }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${isActive ? config.color : 'text-havn-gray-400'}`} />
                <span className={`text-xs font-medium ${isActive ? config.color : 'text-havn-gray-500'}`}>
                  {cat.label}
                </span>
              </div>
              <div className="text-2xl font-display font-semibold text-havn-charcoal">
                {count}
              </div>
            </button>
          )
        })}
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-havn-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search components by name..."
          className="input pl-12 py-3"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-havn-gray-400 hover:text-havn-charcoal"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Component Lists */}
      {filteredComponents && categories.map(cat => {
        if (activeCategory && activeCategory !== cat.id) return null

        const items = filteredComponents[cat.id] || []
        if (items.length === 0) return null

        const config = categoryConfig[cat.id]
        const Icon = cat.icon

        return (
          <div key={cat.id} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Icon className={`w-5 h-5 ${config.color}`} />
              <h3 className="font-display text-lg font-semibold text-havn-green-900">
                {cat.label}
              </h3>
              <span className="text-sm text-havn-gray-400">({items.length})</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((component, i) => (
                <ComponentCard
                  key={i}
                  component={component}
                  category={cat.id}
                  onClick={(c) => {
                    setSelectedComponent(c)
                    setSelectedCategory(cat.id)
                  }}
                />
              ))}
            </div>
          </div>
        )
      })}

      {/* Detail Modal */}
      {selectedComponent && (
        <ComponentDetailModal
          component={selectedComponent}
          category={selectedCategory}
          onClose={() => {
            setSelectedComponent(null)
            setSelectedCategory(null)
          }}
          onNavigate={onNavigate}
        />
      )}
    </div>
  )
}
